/**
 * Data Import Tiles Tab Component
 * Controls which upload tiles are visible on the /data-import page.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Upload } from 'lucide-react';
import type { DataImportTilesVisibility } from './types';

const DATA_IMPORT_TILE_GROUPS: ReadonlyArray<{
  group: string;
  items: ReadonlyArray<{ id: keyof DataImportTilesVisibility; label: string; description: string }>;
}> = [
  {
    group: 'Reference Data',
    items: [
      { id: 'commodity', label: 'Commodity Groups & Types', description: 'Spend categorisation and supplier mapping' },
      { id: 'faultcodes', label: 'Fault Code Categories', description: 'NCR classification' },
      { id: 'departments', label: 'Departments', description: 'User role assignment' },
      { id: 'warehouses', label: 'Warehouses', description: 'Stock planning and delivery routing' },
    ],
  },
  {
    group: 'Supplier Data',
    items: [
      { id: 'suppliers', label: 'Supplier Companies', description: 'Site assignments, currency, OTIF window' },
      { id: 'supplier_additional', label: 'Supplier Additional Details', description: 'Address, commodity, KPI flags' },
    ],
  },
  {
    group: 'Transactional Data',
    items: [
      { id: 'grns', label: 'Goods Receipt Notes (GRNs)', description: 'Historical delivery data' },
      { id: 'pos', label: 'Purchase Orders (Open)', description: 'Open POs with line items' },
      { id: 'ncrs', label: 'Non-Conformance Reports', description: 'Quality issues linked to GRNs' },
      { id: 'forecasts', label: 'Demand Forecasts', description: 'Future demand per part/supplier' },
    ],
  },
  {
    group: 'Configuration',
    items: [
      { id: 'users', label: 'User Accounts', description: 'Create user accounts with permissions' },
    ],
  },
] as const;

interface DataImportTilesTabProps {
  dataImportTiles: DataImportTilesVisibility;
  saving: boolean;
  onDataImportTilesChange: (tiles: DataImportTilesVisibility) => void;
  onSave: () => void;
}

export function DataImportTilesTab({
  dataImportTiles,
  saving,
  onDataImportTilesChange,
  onSave,
}: DataImportTilesTabProps) {
  const totalCount = Object.keys(dataImportTiles).length;
  const enabledCount = Object.values(dataImportTiles).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Data Import Tiles
          </CardTitle>
          <CardDescription>
            Hide upload tiles on the Data Import page that aren't relevant for this tenant.
          </CardDescription>
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-muted-foreground">Upload Tile Visibility</span>
            <Badge variant="secondary">{enabledCount} / {totalCount} Enabled</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {DATA_IMPORT_TILE_GROUPS.map(({ group, items }) => (
            <div key={group} className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">{group}</h4>
              <div className="grid gap-3">
                {items.map(({ id, label, description }) => (
                  <div key={id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div>
                      <Label className="text-sm font-medium">{label}</Label>
                      <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                    <Switch
                      checked={dataImportTiles[id] ?? true}
                      onCheckedChange={(checked) => onDataImportTilesChange({ ...dataImportTiles, [id]: checked })}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <Button onClick={onSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Data Import Tile Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
